<template>
  <div class="video-carousel">
    <!-- Left Arrow -->
    <SwitchBtn
      v-if="showArrow && infos.length > 1 && is2DHuman"
      variant="white-large"
      type="prev"
      class="video-carousel__arrow video-carousel__arrow--left"
      @click="prev"
    >
    </SwitchBtn>
    <div class="video-carousel__tabs" v-if="!is2DHuman">
      <div
        v-for="(item, index) in infos"
        :key="item.title"
        :style="{
          '--data-percent': activeIndex === index && showProgress ? 1 - progress : 1,
        }"
        :class="{ 'video-carousel__tab-item--active': activeIndex === index }"
        class="video-carousel__tab-item"
        @click="goTo(index)"
      >
        <p class="video-carousel__tab-title">{{ item.title }}</p>
        <p v-if="item.tabDesc" class="video-carousel__tab-desc">
          {{ item.tabDesc }}
        </p>
      </div>
    </div>
    <div class="video-carousel__video-wrapper">
      <VideoPlayer
        ref="videoRef"
        :key="activeIndex"
        class="video-carousel__video"
        :url="src"
        :poster="poster"
        :autoplay="true"
        :loop="false"
        :controls="showControls"
        :muted="isMuted"
        :enable-fullscreen="false"
        loading="lazy"
        @ended="next"
        @time-update="timeUpdate"
      />
    </div>
    <!-- Right Arrow -->
    <SwitchBtn
      v-if="showArrow && infos.length > 1"
      variant="white-large"
      type="next"
      class="video-carousel__arrow video-carousel__arrow--right"
      @click="next"
    />
    <div class="video-carousel__dots">
      <Dot
        v-for="(item, index) in infos"
        :key="item.title"
        :active="activeIndex === index"
        @click="goTo(index)"
      />
    </div>
    <div class="video-carousel__content" v-if="infos[activeIndex].desc">
      <h3 v-if="infos[activeIndex].title" class="video-carousel__title">
        {{ infos[activeIndex].subTitle }}
      </h3>
      <p class="video-carousel__desc">{{ infos[activeIndex].desc }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import VideoPlayer from '~/components/video-player/index.vue';
import Dot from '~/components/xDot/index.vue';
import SwitchBtn from '~/design/components/buttons/switch-btn.vue';

const props = withDefaults(
  defineProps<{
    infos: {
      title?: string;
      url: string;
      poster?: string;
      desc?: string;
      subTitle?: string;
      tabDesc?: string;
    }[];
    // 左右切换箭头
    showArrow?: boolean;
    // Tab上的进度条
    showProgress?: boolean;
    // 视频播放控件
    showControls?: boolean;
    // 是否是2D人物
    is2DHuman?: boolean;
  }>(),
  {
    showProgress: true,
    showControls: true,
    showArrow: false,
  },
);

const activeIndex = ref(0);
const videoRef = ref();
const src = ref(props.infos[activeIndex.value].url);
const poster = ref(props.infos[activeIndex.value].poster);

const progress = ref(0);

const isMuted = inject<boolean>('isMuted') || true;

const goTo = (index: number) => {
  activeIndex.value = index;
};

watch(
  () => activeIndex.value,
  val => {
    src.value = props.infos[val].url;
    poster.value = props.infos[val].poster;
  },
);

function next() {
  if (activeIndex.value === props.infos.length - 1) {
    activeIndex.value = 0;
  } else {
    activeIndex.value++;
  }
}

function prev() {
  if (activeIndex.value === 0) {
    activeIndex.value = props.infos.length - 1;
  } else {
    activeIndex.value--;
  }
}

function timeUpdate(currentTime: number) {
  progress.value = currentTime / videoRef.value.duration();
}
</script>

<style lang="scss" scoped>
.video-carousel {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .video-carousel__tabs {
    display: flex;
    gap: 16px;
    margin-bottom: 40px;
  }

  .video-carousel__video-wrapper {
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
    aspect-ratio: 16 / 9;
    position: relative;
  }

  .video-carousel__video {
    width: 100%;
    height: 100%;

    :deep(video) {
      object-fit: cover;
    }
  }

  .video-carousel__tab-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    min-width: 146px;
    gap: 8px;
    position: relative;
    font-size: 14px;
    line-height: 14px;
    background-color: var(--x-white-1);
    color: var(--x-text-3);
    border-radius: 22px;
    cursor: pointer;
    overflow: hidden;

    p {
      margin: 0;
    }

    &::before {
      opacity: 0.2;
      content: ' ';
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
      background-color: white;
      transition: transform 0.3s linear;
      transform: translateX(calc(var(--data-percent) * -100%));
    }

    &:hover {
      background-color: var(--x-back-5);

      .video-carousel__tab-title {
        color: var(--x-text-3);
      }
      .video-carousel__tab-desc {
        color: var(--x-text-5);
      }
    }

    &.video-carousel__tab-item--active {
      background-color: var(--x-text-1);
      color: var(--x-white-1);

      .video-carousel__tab-title {
        color: var(--x-white-1);
      }
      .video-carousel__tab-desc {
        color: var(--x-white-5);
      }
    }
  }

  .video-carousel__tab-title {
    color: var(--x-text-3);
    text-align: center;
    font-size: 14px;
    font-style: normal;
    line-height: normal;
  }

  .video-carousel__tab-desc {
    color: var(--x-text-5);
    text-align: center;
    font-size: 14px;
    font-style: normal;
    line-height: normal;
  }

  .video-carousel__dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }

  .video-carousel__content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    margin-top: 16px;
  }

  .video-carousel__title {
    color: var(--x-text-1);
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin: 0;
  }

  .video-carousel__desc {
    margin: 0;
    color: var(--x-text-3);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px; /* 200% */
    white-space: pre-line;
  }

  .video-carousel__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-1px);
    width: 54px;
    height: 54px;
    z-index: 3;
    &--left {
      left: -90px;
    }

    &--right {
      right: -90px;
    }
  }
}
</style>
